import { Card, Col, Row, Typography } from 'antd'
import { format } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import { SUB_DRA_MAPPINGS } from 'features/indicationTriage'
import { ReviewSection, ReviewSectionProps } from 'features/reporting/types'
import { ClientProvidedImagesData } from 'pages/reporting/reports/raut/rautReportData'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import { SignoffSection } from 'pages/reporting/shared/components/SignOffSection'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementRange } from 'types'

const Text = Typography.Text

/**
 * ReviewPageSection - Card container for review sections with subtle elevation
 * Uses theme tokens for consistent styling across light/dark themes
 */
const ReviewPageSection = styled(Card)`
  && {
    margin-bottom: 1.5rem;
    background: ${({ theme }) =>
      theme.forecast.cardBackground || theme.forecast.background1};
    border: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
    border-radius: 0.75rem;
    box-shadow: 0 0.125rem 0.5rem
      ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.06)'};
    overflow: hidden;

    .ant-card-head {
      border-bottom: 1px solid
        ${({ theme }) =>
          theme.forecast.cardDividerBackground || theme.forecast.border};
      padding: 0 1.5rem;
      min-height: 3.5rem;
      background: ${({ theme }) =>
        theme.forecast.cardGroupBackground || 'transparent'};
    }

    .ant-card-head-title {
      font-weight: 600;
      font-size: 0.9375rem;
      color: ${({ theme }) => theme.forecast.activeText};
      padding: 1rem 0;
    }

    .ant-card-body {
      padding: 1.5rem;
    }
  }
`

/**
 * SectionContent - Wrapper for section content with consistent left spacing
 */
const SectionContent = styled.div`
  padding-left: 0.5rem;
`

/**
 * DataRow - Row layout for label/value pairs with proper spacing
 */
const DataRow = styled.div`
  margin-bottom: 0.625rem;
  display: flex;
  gap: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.forecast.activeText};

  .ant-typography {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

/**
 * DataLabel - Strong text styling for data labels
 */
const DataLabel = styled(Text)`
  && {
    font-weight: 500;
    color: ${({ theme }) => theme.forecast.secondaryText};
    min-width: fit-content;
  }
`

/**
 * SubSection - Nested content area with visual grouping
 */
const SubSection = styled.div`
  margin-left: 1.25rem;
  padding-left: 0.75rem;
  border-left: 2px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
`

/**
 * TechniqueBlock - Visual container for technique-related data
 */
const TechniqueBlock = styled.div`
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  background: ${({ theme }) =>
    theme.forecast.cardGroupBackground || 'rgba(0, 0, 0, 0.02)'};
  border-radius: 0.5rem;
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
`

/**
 * TechniqueTitle - Title styling for technique blocks
 */
const TechniqueTitle = styled(Text)`
  && {
    font-size: 0.9375rem;
    font-weight: 600;
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

/**
 * SignoffBlock - Container for signoff information with subtle highlight
 */
const SignoffBlock = styled.div`
  margin-bottom: 1.25rem;
  padding: 1rem 1.25rem;
  background: ${({ theme }) =>
    theme.forecast.blueGlassBackground || 'rgba(24, 144, 255, 0.06)'};
  border-radius: 0.5rem;
  border: 1px solid
    ${({ theme }) => theme.forecast.primary || 'rgba(24, 144, 255, 0.2)'};

  &:last-child {
    margin-bottom: 0;
  }
`

/**
 * SignoffTitle - Title for signoff blocks
 */
const SignoffTitle = styled(Text)`
  && {
    display: block;
    font-weight: 600;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.forecast.activeText};
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
  }
`

/**
 * CalibrationTableHeader - Header row for calibration step tables
 */
const CalibrationTableHeader = styled(Row)`
  && {
    margin-left: 1.25rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
    margin-bottom: 0.375rem;

    .ant-typography {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: ${({ theme }) => theme.forecast.tertiaryText};
    }
  }
`

/**
 * CalibrationTableRow - Data row for calibration step tables
 */
const CalibrationTableRow = styled(Row)`
  && {
    margin-left: 1.25rem;
    padding: 0.375rem 0;

    &:not(:last-child) {
      border-bottom: 1px solid
        ${({ theme }) =>
          theme.forecast.cardDividerBackground || theme.forecast.border};
    }

    .ant-typography {
      font-size: 0.8125rem;
      color: ${({ theme }) => theme.forecast.activeText};
    }
  }
`

/**
 * SectionDivider - Visual separator between major subsections
 */
const SectionDivider = styled.div`
  margin: 1.25rem 0;
  border-top: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
`

/**
 * WeldScanBlock - Container for individual weld scan entries
 */
const WeldScanBlock = styled.div`
  margin-top: 1.25rem;
  padding: 1rem;
  background: ${({ theme }) =>
    theme.forecast.cardGroupBackground || 'rgba(0, 0, 0, 0.02)'};
  border-radius: 0.5rem;
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
`

/**
 * ImageGroupBlock - Container for grouped image entries
 */
const ImageGroupBlock = styled.div`
  margin-top: 0.75rem;
`

type DateValue = Dayjs | string | Date | undefined | null

const formatDate = (date: DateValue) => {
  if (!date) return 'Not specified'

  if (date instanceof Date) {
    return format(date, 'MM/dd/yyyy')
  }

  if (typeof date === 'string') {
    return format(new Date(date), 'MM/dd/yyyy')
  }

  if (date.toDate && typeof date.toDate === 'function') {
    return format(date.toDate(), 'MM/dd/yyyy')
  }

  return 'Invalid date'
}

const formatNominals = (
  nominalsByInspection: ReadonlyMap<
    string,
    GeckoMeasurement | GeckoMeasurementRange
  >
): string => {
  if (nominalsByInspection.size === 0) return 'Not specified'

  const nominals = Array.from(nominalsByInspection.values()).map((nominal) => {
    if ('start' in nominal) {
      return `${nominal.start.num} ${nominal.start.unit.name}`
    } else {
      return `${nominal.num} ${nominal.unit.name}`
    }
  })

  return [...new Set(nominals)].join(', ')
}

const formatScanCounts = (scanCount: ReadonlyMap<string, number>): number => {
  return Array.from(scanCount.values()).reduce((sum, count) => sum + count, 0)
}

const RenderReact: React.FC<ReviewSectionProps> = ({ previewMode }) => {
  const {
    formData,
    handleSignoff,
    selectedRevisionData,
    isLoading,
    lockState,
    handleStartSignoff,
    handleUnlock,
    metadataEntryId,
    uniqueScanTypesString,
    nominalsByInspection,
    scanCount,
    totalUT,
    scanArea,
    majorDamageRating,
    completionStatus,
  } = useRautReportOps()

  const selectedFormData = useMemo(
    () =>
      previewMode && selectedRevisionData ? selectedRevisionData : formData,
    [formData, selectedRevisionData, previewMode]
  )

  const {
    mainInfo,
    coverPageData,
    summaryPageData,
    calibrationData,
    clientProvidedImagesData,
    signoff,
    revisionMetadata,
  } = selectedFormData

  const safeClientProvidedImagesData = useMemo(
    (): ClientProvidedImagesData => ({
      images: clientProvidedImagesData?.images || [],
    }),
    [clientProvidedImagesData]
  )

  // Get signature dates for display
  const supervisorSignDate = signoff?.supervisorSignoff?.timestamp
    ? new Date(signoff.supervisorSignoff.timestamp)
    : undefined

  const reviewerSignDate = signoff?.reviewerSignoff?.timestamp
    ? new Date(signoff.reviewerSignoff.timestamp)
    : undefined

  const canLock = useMemo(() => {
    return Object.entries(completionStatus)
      .filter(([key]) => key !== 'review')
      .every(([, status]) => status.isComplete)
  }, [completionStatus])

  return (
    <div>
      <ReviewPageSection title='Cover Page'>
        <SectionContent>
          <DataRow>
            <DataLabel strong>Customer Name:</DataLabel> {mainInfo.customerName}
          </DataRow>
          <DataRow>
            <DataLabel strong>Asset:</DataLabel> {mainInfo.asset}
          </DataRow>
          <DataRow>
            <DataLabel strong>Location:</DataLabel> {mainInfo.location}
          </DataRow>
          <DataRow>
            <DataLabel strong>Scan Methods:</DataLabel> {uniqueScanTypesString}
          </DataRow>
          <DataRow>
            <DataLabel strong>Gecko Job ID:</DataLabel>{' '}
            {coverPageData.geckoJobId}
          </DataRow>
          <DataRow>
            <DataLabel strong>Inspection Period:</DataLabel>{' '}
            {coverPageData.startDate && coverPageData.endDate
              ? `${formatDate(coverPageData.startDate)} - ${formatDate(
                  coverPageData.endDate
                )}`
              : 'Not specified'}
          </DataRow>
          <DataRow>
            <DataLabel strong>Revision Number:</DataLabel>{' '}
            {revisionMetadata.revisionNumber}
          </DataRow>
          <DataRow>
            <DataLabel strong>Publication Status:</DataLabel>{' '}
            {revisionMetadata.isPublished ? 'Published' : 'Draft'}
          </DataRow>

          <SectionDivider />

          {/* Display supervisor information from signoff */}
          <DataRow>
            <DataLabel strong>Inspection Supervisor:</DataLabel>{' '}
            {signoff?.supervisorSignoff?.name || 'Not signed'}
          </DataRow>
          {signoff?.supervisorSignoff && (
            <SubSection>
              <DataRow>
                <DataLabel strong>Qualifications:</DataLabel>{' '}
                {signoff.supervisorSignoff.qualifications}
              </DataRow>
              <DataRow>
                <DataLabel strong>Signoff Date:</DataLabel>{' '}
                {formatDate(supervisorSignDate)}
              </DataRow>
            </SubSection>
          )}

          {/* Display reviewer information from signoff */}
          <DataRow>
            <DataLabel strong>Reviewer:</DataLabel>{' '}
            {signoff?.reviewerSignoff?.name || 'Not signed'}
          </DataRow>
          {signoff?.reviewerSignoff && (
            <SubSection>
              <DataRow>
                <DataLabel strong>Qualifications:</DataLabel>{' '}
                {signoff.reviewerSignoff.qualifications}
              </DataRow>
              <DataRow>
                <DataLabel strong>Review Date:</DataLabel>{' '}
                {formatDate(reviewerSignDate)}
              </DataRow>
            </SubSection>
          )}
        </SectionContent>
      </ReviewPageSection>

      <ReviewPageSection title='Summary Page'>
        <SectionContent>
          <DataRow>
            <DataLabel strong>Material:</DataLabel> {mainInfo.material}
          </DataRow>
          <DataRow>
            <DataLabel strong>Nominal Thickness:</DataLabel>{' '}
            {formatNominals(nominalsByInspection)}
          </DataRow>
          <DataRow>
            <DataLabel strong>Surface Temperature:</DataLabel>{' '}
            {mainInfo.surfaceTemperature ?? 'Not specified'}{' '}
            {mainInfo.surfaceTemperature ? mainInfo.temperatureUnit : ''}
          </DataRow>
          <DataRow>
            <DataLabel strong>Method Coverage Scope:</DataLabel>{' '}
            {mainInfo.scopesByScanType
              ? Object.entries(mainInfo.scopesByScanType)
                  .filter((entry) => entry[0])
                  .map(([type, scope]) => `${type}: ${scope}`)
                  .join(', ') || 'Not specified'
              : 'Not specified'}
          </DataRow>
          <DataRow>
            <DataLabel strong>Inspection Scope:</DataLabel>{' '}
            {summaryPageData.inspectionScope}
          </DataRow>
          {summaryPageData.inspectionSummary && (
            <>
              <SectionDivider />
              <DataRow>
                <DataLabel strong>Number of Scans:</DataLabel>{' '}
                {formatScanCounts(scanCount)}
              </DataRow>
              <DataRow>
                <DataLabel strong>Number of UT Readings:</DataLabel>{' '}
                {totalUT.toLocaleString()}
              </DataRow>
              <DataRow>
                <DataLabel strong>Asset Coverage:</DataLabel>{' '}
                {(scanArea * 100).toFixed(2)}%
              </DataRow>
              <DataRow>
                <DataLabel strong>Damage Rating:</DataLabel>{' '}
                {majorDamageRating
                  ? `${SUB_DRA_MAPPINGS[majorDamageRating]} (${majorDamageRating})`
                  : 'Not specified'}
              </DataRow>
              <DataRow>
                <DataLabel strong>Damage Description:</DataLabel>{' '}
                {majorDamageRating
                  ? `DRA rating: ${majorDamageRating}`
                  : 'Not specified'}
              </DataRow>
              {summaryPageData.inspectionSummary.areas?.[0] && (
                <div>
                  <SectionDivider />
                  <DataRow>
                    <DataLabel strong>Area</DataLabel>
                  </DataRow>
                  <SubSection>
                    <DataRow>
                      <DataLabel strong>Min Thickness:</DataLabel>{' '}
                      {
                        summaryPageData.inspectionSummary.areas[0].minThickness
                          .num
                      }{' '}
                      {summaryPageData.inspectionSummary.thicknessUnit}
                    </DataRow>
                    <DataRow>
                      <DataLabel strong>Avg Thickness:</DataLabel>{' '}
                      {
                        summaryPageData.inspectionSummary.areas[0].avgThickness
                          .num
                      }{' '}
                      {summaryPageData.inspectionSummary.thicknessUnit}
                    </DataRow>
                    <DataRow>
                      <DataLabel strong>Scan Number:</DataLabel>{' '}
                      {summaryPageData.inspectionSummary.areas[0].scanNumber}
                    </DataRow>
                  </SubSection>
                </div>
              )}
              <SectionDivider />
              <DataRow>
                <DataLabel strong>Summary:</DataLabel>{' '}
                {summaryPageData.inspectionSummary.summary}
              </DataRow>
              <DataRow>
                <DataLabel strong>Disclaimer:</DataLabel>{' '}
                {summaryPageData.inspectionSummary.disclaimer}
              </DataRow>
            </>
          )}
        </SectionContent>
      </ReviewPageSection>

      <ReviewPageSection title='Calibration Information'>
        <SectionContent>
          {/* Shared Equipment Information */}
          <DataRow>
            <DataLabel strong>Testing Unit:</DataLabel>{' '}
            {calibrationData.testingUnit}
          </DataRow>
          <DataRow>
            <DataLabel strong>Model:</DataLabel> {calibrationData.model}
          </DataRow>
          <DataRow>
            <DataLabel strong>Instrument S/N:</DataLabel>{' '}
            {calibrationData.instrumentSN}
          </DataRow>
          <DataRow>
            <DataLabel strong>Instrument Calibration Date:</DataLabel>{' '}
            {calibrationData.instrumentCalibrationDate
              ? formatDate(calibrationData.instrumentCalibrationDate)
              : 'Not specified'}
          </DataRow>
          <DataRow>
            <DataLabel strong>Scanner Type:</DataLabel>{' '}
            {calibrationData.scannerType}
          </DataRow>
          <DataRow>
            <DataLabel strong>Scanner S/N:</DataLabel>{' '}
            {calibrationData.scannerSN}
          </DataRow>
          <DataRow>
            <DataLabel strong>Inspection Surface:</DataLabel>{' '}
            {mainInfo.inspectionSurface}
          </DataRow>
          <DataRow>
            <DataLabel strong>Surface Condition:</DataLabel>{' '}
            {mainInfo.surfaceCondition}
          </DataRow>
          <DataRow>
            <DataLabel strong>Procedure:</DataLabel> {calibrationData.procedure}
          </DataRow>
          <DataRow>
            <DataLabel strong>Scan Methods:</DataLabel>{' '}
            {calibrationData.scanMethods}
          </DataRow>

          <SectionDivider />

          {/* Motor Settings */}
          <DataRow>
            <DataLabel strong>X-Motor Settings:</DataLabel>
          </DataRow>
          <SubSection>
            <DataRow>
              <DataLabel strong>Function:</DataLabel>{' '}
              {calibrationData.xMotor?.function || 'Not specified'}
            </DataRow>
            <DataRow>
              <DataLabel strong>Scan Resolution:</DataLabel>{' '}
              {calibrationData.xMotor?.scanRes?.num}{' '}
              {calibrationData.xMotor?.scanRes?.unit?.name}
            </DataRow>
            <DataRow>
              <DataLabel strong>Speed:</DataLabel>{' '}
              {calibrationData.xMotor?.speed?.num}{' '}
              {calibrationData.xMotor?.speed?.unit?.name}/sec
            </DataRow>
          </SubSection>
          <DataRow>
            <DataLabel strong>Y-Motor Settings:</DataLabel>
          </DataRow>
          <SubSection>
            <DataRow>
              <DataLabel strong>Function:</DataLabel>{' '}
              {calibrationData.yMotor?.function || 'Not specified'}
            </DataRow>
            <DataRow>
              <DataLabel strong>Scan Resolution:</DataLabel>{' '}
              {calibrationData.yMotor?.scanRes?.num}{' '}
              {calibrationData.yMotor?.scanRes?.unit?.name}
            </DataRow>
            <DataRow>
              <DataLabel strong>Speed:</DataLabel>{' '}
              {calibrationData.yMotor?.speed?.num}{' '}
              {calibrationData.yMotor?.speed?.unit.name}/sec
            </DataRow>
          </SubSection>

          {/* Techniques */}
          {calibrationData.techniques?.map((technique, index) => (
            <TechniqueBlock key={technique.id || index}>
              <DataRow>
                <TechniqueTitle strong>
                  Technique {index + 1}: {technique.techniqueName || 'Unnamed'}
                </TechniqueTitle>
              </DataRow>
              <SubSection>
                <DataRow>
                  <DataLabel strong>Probe Type:</DataLabel>{' '}
                  {technique.probeType}
                </DataRow>
                <DataRow>
                  <DataLabel strong>Probe Part #:</DataLabel>{' '}
                  {technique.probePartNumber}
                </DataRow>
                <DataRow>
                  <DataLabel strong>Probe S/N:</DataLabel> {technique.probeSN}
                </DataRow>
                <DataRow>
                  <DataLabel strong>Wedge Type:</DataLabel>{' '}
                  {technique.wedgeType}
                </DataRow>
                <DataRow>
                  <DataLabel strong>Wave Mode:</DataLabel> {technique.waveMode}
                </DataRow>
                <DataRow>
                  <DataLabel strong>Frequency:</DataLabel> {technique.frequency}
                </DataRow>
                <DataRow>
                  <DataLabel strong>Element Quantity:</DataLabel>{' '}
                  {technique.elementQuantity}
                </DataRow>
                <DataRow>
                  <DataLabel strong>Pitch:</DataLabel> {technique.pitch?.num}{' '}
                  {technique.pitch?.unit?.name}
                </DataRow>
                <DataRow>
                  <DataLabel strong>Angle:</DataLabel> {technique.angle}
                </DataRow>
                <DataRow>
                  <DataLabel strong>Scanning Gain:</DataLabel>{' '}
                  {technique.scanningGain} dB
                </DataRow>
                <DataRow>
                  <DataLabel strong>Velocity:</DataLabel>{' '}
                  {technique.velocity?.num} {technique.velocity?.unit?.name}/µs
                </DataRow>

                {/* Per-technique calibration */}
                {technique.calibration && (
                  <>
                    <SectionDivider />
                    <DataRow>
                      <DataLabel strong>Calibration Details:</DataLabel>
                    </DataRow>
                    <SubSection>
                      <DataRow>
                        <DataLabel strong>Cal Block Type:</DataLabel>{' '}
                        {technique.calibration.calBlockType}
                      </DataRow>
                      <DataRow>
                        <DataLabel strong>Cal Block Material:</DataLabel>{' '}
                        {mainInfo.material}
                      </DataRow>
                      <DataRow>
                        <DataLabel strong>Cal Block S/N:</DataLabel>{' '}
                        {technique.calibration.calBlockSN}
                      </DataRow>
                      <DataRow>
                        <DataLabel strong>Reflector Type:</DataLabel>{' '}
                        {technique.calibration.reflectorType}
                      </DataRow>
                    </SubSection>

                    {/* 0° Calibration (for Rapid AUT) */}
                    {technique.techniqueName?.includes('Rapid AUT') &&
                      (technique.calibration.thinStepDistance ||
                        technique.calibration.thickStepDistance ||
                        technique.calibration.verificationStepDistance) && (
                        <>
                          <SectionDivider />
                          <DataRow>
                            <DataLabel strong>0° Calibration Steps:</DataLabel>
                          </DataRow>
                          <CalibrationTableHeader gutter={16}>
                            <Col span={8}>
                              <Text strong>Step</Text>
                            </Col>
                            <Col span={8}>
                              <Text strong>Distance</Text>
                            </Col>
                            <Col span={8}>
                              <Text strong>Tolerance</Text>
                            </Col>
                          </CalibrationTableHeader>
                          {technique.calibration.thinStepDistance && (
                            <CalibrationTableRow gutter={16}>
                              <Col span={8}>
                                <Text>0° Thin Step</Text>
                              </Col>
                              <Col span={8}>
                                <Text>
                                  {technique.calibration.thinStepDistance.num}{' '}
                                  {
                                    technique.calibration.thinStepDistance.unit
                                      ?.name
                                  }
                                </Text>
                              </Col>
                              <Col span={8}>
                                <Text>
                                  ±{' '}
                                  {technique.calibration.thinStepTolerance?.num}{' '}
                                  {
                                    technique.calibration.thinStepTolerance
                                      ?.unit?.name
                                  }
                                </Text>
                              </Col>
                            </CalibrationTableRow>
                          )}
                          {technique.calibration.thickStepDistance && (
                            <CalibrationTableRow gutter={16}>
                              <Col span={8}>
                                <Text>0° Thick Step</Text>
                              </Col>
                              <Col span={8}>
                                <Text>
                                  {technique.calibration.thickStepDistance.num}{' '}
                                  {
                                    technique.calibration.thickStepDistance.unit
                                      ?.name
                                  }
                                </Text>
                              </Col>
                              <Col span={8}>
                                <Text>
                                  ±{' '}
                                  {
                                    technique.calibration.thickStepTolerance
                                      ?.num
                                  }{' '}
                                  {
                                    technique.calibration.thickStepTolerance
                                      ?.unit?.name
                                  }
                                </Text>
                              </Col>
                            </CalibrationTableRow>
                          )}
                          {technique.calibration.verificationStepDistance && (
                            <CalibrationTableRow gutter={16}>
                              <Col span={8}>
                                <Text>Verification Step</Text>
                              </Col>
                              <Col span={8}>
                                <Text>
                                  {
                                    technique.calibration
                                      .verificationStepDistance.num
                                  }{' '}
                                  {
                                    technique.calibration
                                      .verificationStepDistance.unit?.name
                                  }
                                </Text>
                              </Col>
                              <Col span={8}>
                                <Text>
                                  ±{' '}
                                  {
                                    technique.calibration
                                      .verificationStepTolerance?.num
                                  }{' '}
                                  {
                                    technique.calibration
                                      .verificationStepTolerance?.unit?.name
                                  }
                                </Text>
                              </Col>
                            </CalibrationTableRow>
                          )}
                        </>
                      )}
                  </>
                )}
              </SubSection>
            </TechniqueBlock>
          ))}

          {/* Global Angle Beam Calibration (for PAUT) */}
          {calibrationData.angleBeamCalibration &&
            calibrationData.angleBeamCalibration.length > 0 && (
              <>
                <SectionDivider />
                <DataRow>
                  <DataLabel strong>Angle Beam Calibration:</DataLabel>
                </DataRow>
                <SubSection>
                  {calibrationData.angleBeamCalibration.map((entry, index) => (
                    <DataRow key={entry.id || index}>
                      <DataLabel strong>{entry.name}:</DataLabel>{' '}
                      {entry.distance?.num} {entry.distance?.unit?.name},
                      Amplitude: {entry.amplitude}
                    </DataRow>
                  ))}
                </SubSection>
              </>
            )}

          {/* Calibration Verification */}
          <SectionDivider />
          <DataRow>
            <DataLabel strong>Calibration Verification Dates:</DataLabel>
          </DataRow>
          <SubSection>
            {calibrationData.calibrationVerification?.map(
              (verification, index) => (
                <DataRow key={index}>
                  <DataLabel strong>{`Verification ${index + 1}:`}</DataLabel>{' '}
                  {verification.date
                    ? dayjs.isDayjs(verification.date)
                      ? verification.date.format('MM/DD/YYYY HH:mm:ss')
                      : dayjs(verification.date).format('MM/DD/YYYY HH:mm:ss')
                    : 'No date specified'}
                </DataRow>
              )
            )}
          </SubSection>

          {/* Calibration Image */}
          <SectionDivider />
          <DataRow>
            <DataLabel strong>Calibration Image:</DataLabel>{' '}
            {calibrationData.calibrationImage?.imagePath
              ? 'Provided'
              : 'Not provided'}
          </DataRow>
          {calibrationData.calibrationImage?.imageCaption && (
            <DataRow>
              <DataLabel strong>Calibration Image Caption:</DataLabel>{' '}
              {calibrationData.calibrationImage.imageCaption}
            </DataRow>
          )}
        </SectionContent>
      </ReviewPageSection>

      {/* PAUT Weld Examination Section */}
      {selectedFormData.weldScanData &&
        selectedFormData.weldScanData.scans &&
        selectedFormData.weldScanData.scans.length > 0 && (
          <ReviewPageSection title='PAUT Weld Examination'>
            <SectionContent>
              <DataRow>
                <DataLabel strong>Total Weld Scans:</DataLabel>{' '}
                {selectedFormData.weldScanData.scans.length}
              </DataRow>
              <DataRow>
                <DataLabel strong>Total Weld Indications:</DataLabel>{' '}
                {selectedFormData.weldScanData.scans.reduce(
                  (sum, scan) => sum + (scan.totalIndications || 0),
                  0
                )}
              </DataRow>

              {selectedFormData.weldScanData.scans.map((scan, index) => (
                <WeldScanBlock key={scan.id || index}>
                  <DataRow>
                    <TechniqueTitle strong>
                      {scan.scanLocation} - {scan.scanLabel}
                    </TechniqueTitle>
                  </DataRow>
                  <SubSection>
                    <DataRow>
                      <DataLabel strong>Scan Start:</DataLabel>{' '}
                      {scan.scanStart
                        ? `${scan.scanStart.num} ${scan.scanStart.unit.name}`
                        : 'Not specified'}
                    </DataRow>
                    <DataRow>
                      <DataLabel strong>Scan Stop:</DataLabel>{' '}
                      {scan.scanStop
                        ? `${scan.scanStop.num} ${scan.scanStop.unit.name}`
                        : 'Not specified'}
                    </DataRow>
                    <DataRow>
                      <DataLabel strong>Centerline Negative:</DataLabel>{' '}
                      {scan.centerlineNeg
                        ? `${scan.centerlineNeg.num} ${scan.centerlineNeg.unit.name}`
                        : 'Not specified'}
                    </DataRow>
                    <DataRow>
                      <DataLabel strong>Centerline Positive:</DataLabel>{' '}
                      {scan.centerlinePos
                        ? `${scan.centerlinePos.num} ${scan.centerlinePos.unit.name}`
                        : 'Not specified'}
                    </DataRow>
                    <DataRow>
                      <DataLabel strong>Total Indications:</DataLabel>{' '}
                      {scan.totalIndications ?? 0}
                    </DataRow>
                    {scan.comments && (
                      <DataRow>
                        <DataLabel strong>Comments:</DataLabel> {scan.comments}
                      </DataRow>
                    )}
                  </SubSection>
                </WeldScanBlock>
              ))}
            </SectionContent>
          </ReviewPageSection>
        )}

      <ReviewPageSection title='Client Provided Images'>
        <SectionContent>
          {safeClientProvidedImagesData.images &&
          safeClientProvidedImagesData.images.length > 0 ? (
            <>
              <DataRow>
                <DataLabel strong>Total Images:</DataLabel>{' '}
                {safeClientProvidedImagesData.images.length}
              </DataRow>

              <SectionDivider />

              {/* Group images by type */}
              {Object.entries(
                safeClientProvidedImagesData.images.reduce(
                  (acc, img) => {
                    if (!acc[img.type]) {
                      acc[img.type] = []
                    }
                    acc[img.type].push(img)
                    return acc
                  },
                  {} as Record<
                    string,
                    typeof safeClientProvidedImagesData.images
                  >
                )
              ).map(([type, images]) => (
                <ImageGroupBlock key={type}>
                  <DataRow>
                    <DataLabel strong>
                      {type.charAt(0).toUpperCase() + type.slice(1)}s:
                    </DataLabel>{' '}
                    {images.length}
                  </DataRow>
                  <SubSection>
                    {images.map((image, index) => (
                      <div key={image.id}>
                        <DataRow>
                          <DataLabel
                            strong>{`${type.charAt(0).toUpperCase() + type.slice(1)} ${index + 1}:`}</DataLabel>{' '}
                          {image.imagePath ? 'Provided' : 'Not provided'}
                        </DataRow>
                        {image.imageCaption && (
                          <DataRow>
                            <DataLabel strong>Caption:</DataLabel>{' '}
                            {image.imageCaption}
                          </DataRow>
                        )}
                      </div>
                    ))}
                  </SubSection>
                </ImageGroupBlock>
              ))}
            </>
          ) : (
            <DataRow>
              <Text>No client-provided images available</Text>
            </DataRow>
          )}
        </SectionContent>
      </ReviewPageSection>
      {/* Only show SignoffSection if not in preview mode */}
      {!previewMode && (
        <SignoffSection
          currentSignoff={signoff}
          onSignoff={handleSignoff}
          loading={isLoading}
          isLocked={lockState.isLocked}
          onStartSignoff={handleStartSignoff}
          onUnlock={handleUnlock}
          publishedPdfPath={formData.publishedPdfPath}
          metadataEntryId={metadataEntryId}
          canLock={canLock}
          isPublished={revisionMetadata.isPublished}
        />
      )}
      {/* If in preview mode and has signoff, show read-only signoff info */}
      {previewMode && signoff && (
        <ReviewPageSection title='Signoff Information'>
          <SectionContent>
            {signoff.supervisorSignoff && (
              <SignoffBlock>
                <SignoffTitle strong>Supervisor Signoff</SignoffTitle>
                <DataRow>
                  <DataLabel>Name:</DataLabel> {signoff.supervisorSignoff.name}
                </DataRow>
                <DataRow>
                  <DataLabel>Email:</DataLabel>{' '}
                  {signoff.supervisorSignoff.email}
                </DataRow>
                <DataRow>
                  <DataLabel>Qualifications:</DataLabel>{' '}
                  {signoff.supervisorSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <DataLabel>Date:</DataLabel>{' '}
                  {format(
                    new Date(signoff.supervisorSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </SignoffBlock>
            )}
            {signoff.reviewerSignoff && (
              <SignoffBlock>
                <SignoffTitle strong>Reviewer Signoff</SignoffTitle>
                <DataRow>
                  <DataLabel>Name:</DataLabel> {signoff.reviewerSignoff.name}
                </DataRow>
                <DataRow>
                  <DataLabel>Email:</DataLabel> {signoff.reviewerSignoff.email}
                </DataRow>
                <DataRow>
                  <DataLabel>Qualifications:</DataLabel>{' '}
                  {signoff.reviewerSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <DataLabel>Date:</DataLabel>{' '}
                  {format(
                    new Date(signoff.reviewerSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </SignoffBlock>
            )}
          </SectionContent>
        </ReviewPageSection>
      )}
    </div>
  )
}

export const ReportReview: ReviewSection = {
  id: 'review',
  title: 'Review',
  description: 'Review report data and complete sign off',
  RenderReact,
  required: true,
}
